'use client';

import type { ProductCategorySummary } from '@eliadmin/core/src/api/products/types/product-types';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { useId, useMemo } from 'react';
import type { FitmentProduct } from '../../api/product-fitments-api';
import { buildFitmentCategoryIndex, groupFitmentProductsByCategory } from './fitment-categories';
import { FitmentVariantSummary } from './fitment-ui';

export function CategorizedFitmentProducts({
  products,
  categoryTree,
  selectedCategoryIds,
}: {
  products: FitmentProduct[];
  categoryTree?: ProductCategorySummary[];
  selectedCategoryIds: number[];
}) {
  const sectionPrefix = useId();
  const categoryIndex = useMemo(() => buildFitmentCategoryIndex(categoryTree), [categoryTree]);
  const groups = useMemo(
    () => groupFitmentProductsByCategory(products, categoryIndex, selectedCategoryIds),
    [products, categoryIndex, selectedCategoryIds],
  );

  return (
    <div className="space-y-8">
      {groups.map(group => {
        const node =
          group.categoryId === null ? undefined : categoryIndex.byId.get(group.categoryId);
        // Imported catalogues can contain distinct categories with the same name.
        const duplicateName = groups.some(
          other => other.key !== group.key && other.name === group.name,
        );
        const ancestorContext = node?.ancestorIds
          .map(id => categoryIndex.byId.get(id)?.category.name)
          .filter(Boolean)
          .join(' / ');
        const context = !duplicateName
          ? null
          : group.categoryId === null
            ? 'Bez priradenej kategórie'
            : [ancestorContext, `Kategória #${group.categoryId}`].filter(Boolean).join(' · ');

        return (
          <section
            key={group.key}
            aria-labelledby={`${sectionPrefix}-${group.key}`}
            className="space-y-4"
          >
            <div className="min-w-0 border-b pb-3">
              <h3
                id={`${sectionPrefix}-${group.key}`}
                className="break-words text-lg font-semibold"
              >
                {group.name}
              </h3>
              {context && (
                <p className="mt-1 break-words text-xs text-muted-foreground">{context}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3">
              {group.products.map(part => (
                <div key={part.id} className="min-w-0 space-y-3">
                  <ProductCard product={part as Product} />
                  <FitmentVariantSummary product={part} />
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
